<script setup>
import { router } from '@inertiajs/vue3'
import TextareaInput from '@/Shared/TextareaInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { useForm } from '@inertiajs/vue3'
import { useConfirm } from '@/Composables/useConfirm'
import ToggleInput from '@/Shared/ToggleInput.vue'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  coach: {
    type: Object,
    required: true
  }
})

let form = useForm({
  is_admin: props.coach.is_admin,
  observations: props.coach.observations
})

function update() {
  form.put(`/coaches/${props.coach.id}/update`)
}

async function remove() {
  if (await useConfirm('¿Estás seguro de que quieres eliminar este entrenador?')) {
    router.delete(`/coaches/${props.coach.id}/delete`)
  }
}

const breadcrumbs = [
  { label: 'Clases' },
  { label: 'Entrenadores', href: '/coaches' },
  { label: props.coach.player.fullname }
]
</script>

<template>
  <Portrait
    :title="`Entrenador: ${coach.player.fullname}`"
    subtitle="Gestiona la información y permisos del entrenador"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6">
      <div class="flex items-center mb-6 pb-4 border-b border-gray-100">
        <div
          class="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xl font-bold mr-4"
        >
          {{ coach.player.name.charAt(0) }}{{ coach.player.lastname.charAt(0) }}
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-900">{{ coach.player.fullname }}</h3>
          <p class="text-sm text-gray-500 flex items-center">
            <Icon name="Phone" class="h-4 w-4 mr-1" />
            {{ coach.player.phone }}
          </p>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="update">
        <div class="grid grid-cols-1 gap-6">
          <div class="bg-gray-50 p-4 rounded-md">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Información</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <div class="bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
                  {{ coach.player.fullname }}
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div class="bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
                  {{ coach.player.email }}
                </div>
              </div>
            </div>
          </div>

          <ToggleInput
            v-model="form.is_admin"
            label="Permisos de administrador"
            description="Otorga permisos de administrador a este entrenador para gestionar el club"
          />

          <TextareaInput
            v-model="form.observations"
            :error="form.errors.observations"
            label="Observaciones"
            placeholder="Añade notas o comentarios sobre este entrenador"
          />
        </div>
      </form>
    </div>

    <template #footer>
      <!-- Versión móvil -->
      <div class="w-full block sm:hidden">
        <div class="flex items-stretch justify-end w-full space-x-4">
          <button class="btn-red" type="button" @click="remove">Eliminar</button>

          <LoadingButton
            :loading="form.processing"
            class="btn-indigo"
            type="submit"
            @click="update"
          >
            Guardar
          </LoadingButton>
        </div>
      </div>

      <!-- Versión escritorio -->
      <div class="hidden sm:flex sm:items-center sm:justify-end w-full space-x-4">
        <button class="btn-red" type="button" @click="remove">Eliminar</button>

        <LoadingButton :loading="form.processing" class="btn-indigo" type="submit" @click="update">
          Guardar
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
